import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, ArrowLeft, Send } from "lucide-react";

export default function ForgotPassword() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/auth/forgot-password");
      setResult(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to send reset email");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1581141444721-0e6f8fa8397e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODA4MTg2Mzd8MA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/admin/login" className="text-sm uppercase tracking-[0.3em] text-amber-300 inline-flex items-center gap-2"><ArrowLeft className="w-3.5 h-3.5" /> Back to login</Link>
          <div>
            <div className="text-amber-300 text-6xl font-serif-en">ﷲ</div>
            <h1 className="font-serif-en text-5xl mt-4">Forgot Password</h1>
            <p className="mt-3 text-stone-200/80 max-w-md">A one-time reset link will be sent to the super-admin email on file.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="forgot-form" className="w-full max-w-sm">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-700 mb-3"><Mail className="w-3.5 h-3.5" /> Reset</div>
            <h2 className="font-serif-en text-3xl text-emerald-950">Recover Access</h2>
            <p className="text-stone-500 mt-1 text-sm">We'll send a secure reset link to the registered super-admin email. The link is valid for 1 hour.</p>
          </div>

          {result ? (
            <div data-testid="forgot-success" className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
              <div className="font-medium text-emerald-900">{result.email_sent ? "Email sent" : "Reset link generated"}</div>
              <p className="text-sm text-stone-700 mt-2">
                {result.email_sent
                  ? <>A reset link has been emailed to <span className="font-mono text-emerald-900">{result.recipient}</span>. Please check the inbox (and spam folder).</>
                  : <>Email service is not configured. The reset link has been logged on the server — ask your developer to retrieve it from the backend logs, or configure Resend in Admin → Security.</>}
              </p>
              <Link to="/admin/login" className="btn-ghost-emerald inline-flex items-center gap-2 mt-4"><ArrowLeft className="w-4 h-4" /> Back to login</Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-stone-600 mb-5">Click below to send a password reset link to the super-admin email.</p>
              {error && <div data-testid="forgot-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-3">{error}</div>}
              <button data-testid="forgot-submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Reset Link"}
              </button>
              <Link to="/admin/login" data-testid="forgot-back" className="mt-5 text-sm text-stone-500 hover:text-emerald-900 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back to login</Link>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
